import { fetchAuthSession } from 'aws-amplify/auth';
import { Buffer } from 'buffer/';
import { toast } from 'react-toastify';
import counties from '../counties.json';
import { GlobalContextProps } from "../global-context";
import { Box, ScreenshotSize } from './CustomTypes';

const General = {
  getYear() {
    return new Date().getFullYear().toString();
  },
  addMonths(months: number) {
    const date = new Date();
    date.setMonth(date.getMonth() + months);
    return date;
  },
  kmDistanceToMilesAway(km: number) {
    const milesAway = Math.round(km * 0.621371);
    if (milesAway < 1) return '0-1'
    if (milesAway < 2) return '1-2'
    if (milesAway < 5) return '2-5'
    if (milesAway < 10) return '5-10'
    if (milesAway < 20) return '10-20'
    if (milesAway < 50) return '20-50'
    if (milesAway < 100) return '50-100'
    return '100+'
  },
  getBoundingBoxPosition(screenshot_size: ScreenshotSize, box: Box) {
    try {
      const ratio = 900 / screenshot_size.width;
      const width = box.width * ratio;
      const height = box.height * ratio;
      const top = box.y * ratio;
      const left = box.x * ratio;
      const averageLeft = left + width / 2;
      const averageTop = top + height / 2;
      return {
        top: `${averageTop}px`,
        left: `${averageLeft}px`,
      }
    } catch {
      return {
        top: '0px',
        left: '0px',
      }
    }
  },
  logAndToastError: (message: string, error: any) => {
    console.error(message, error);
    const message1 = error?.response?.data?.message
    const message2 = error?.message
    const finalMessage = message1 || message2
    toast.warn(`${message}.${finalMessage ? ' ' + General.ellipsisInMiddle(finalMessage, 300) : ''}`);
  },
  daysAgo(dateString: string, format = null) {
    try {
      if (typeof dateString !== 'string') return null

      // Parse the input date string as an ISO date
      const inputDate = new Date(dateString) as any;

      // Get the current date
      const currentDate = new Date() as any;

      // Calculate the difference in time (milliseconds) between the two dates
      const timeDifference = currentDate - inputDate;

      // Convert the time difference from milliseconds to various units
      const daysDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
      const hoursDifference = Math.floor(timeDifference / (1000 * 60 * 60));
      const minutesDifference = Math.floor(timeDifference / (1000 * 60));
      const weeksDifference = Math.floor(daysDifference / 7);
      const monthsDifference = Math.floor(daysDifference / 30);

      if (minutesDifference < 1) return 'just now';
      if (minutesDifference <= 60) return `${minutesDifference} minute${minutesDifference === 1 ? '' : 's'} ago`;
      if (hoursDifference < 24) return `${hoursDifference} hour${ hoursDifference === 1 ? '' : 's' } ago`;
      if (daysDifference === 0) return 'today';
      if (daysDifference < 7) return `${daysDifference} day${ daysDifference === 1 ? '' : 's' } ago`;
      if (weeksDifference < 5) return `${weeksDifference} week${ weeksDifference === 1 ? '' : 's' } ago`;
      return `${monthsDifference} month${ monthsDifference === 1 ? '' : 's' } ago`;
    } catch (err) {
      return null
    }
  },
  daysAgoDetailed(dateString: string, format = null) {
    try {
      if (typeof dateString !== 'string') return null

      // Parse the input date string as an ISO date
      const inputDate = new Date(dateString) as any;

      // Get the current date
      const currentDate = new Date() as any;

      // Calculate the difference in time (milliseconds) between the two dates
      const timeDifference = currentDate - inputDate;

      // Convert the time difference from milliseconds to various units
      const daysDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
      const hoursDifference = Math.floor(timeDifference / (1000 * 60 * 60));
      const minutesDifference = Math.floor(timeDifference / (1000 * 60));
      const remainingMinutes = minutesDifference % 60;
      const weeksDifference = Math.floor(daysDifference / 7);
      const monthsDifference = Math.floor(daysDifference / 30);

      if (minutesDifference <= 60) return `${minutesDifference} min${ minutesDifference === 1 ? '' : 's' } ago`;
      if (hoursDifference < 24) return `${hoursDifference} hr${ hoursDifference === 1 ? '' : 's' }${ remainingMinutes > 0 ? ` ${remainingMinutes} min${ remainingMinutes === 1 ? '' : 's' }` : '' } ago`;
      if (daysDifference === 0) return 'today';
      if (daysDifference < 7) return `${daysDifference} day${ daysDifference === 1 ? '' : 's' } ago`;
      if (weeksDifference < 5) return `${weeksDifference} week${ weeksDifference === 1 ? '' : 's' } ago`;
      return `${monthsDifference} month${ monthsDifference === 1 ? '' : 's' } ago`;
    } catch (err) {
      return null
    }
  },
  replaceMiddleWithDots(s: string, startLen: number, endLen: number, delimiter = ' ... '): string {
    if (startLen + endLen >= s.length) {
      return s;
    }
    return s.slice(0, startLen) + delimiter + s.slice(-endLen);
  },
  getHumanReadableReligiousAffiliation(religiousAffiliation: string) {
    return {
      'AFRICAN_METHODIST_EPISCOPAL': 'African Methodist Episcopal',
      'AMISH': 'Amish',
      'ANGLICAN': 'Anglican',
      'ASSEMBLY_OF_GOD': 'Assembly of God',
      'BAPTIST': 'Baptist',
      'BRETHREN': 'Brethren',
      'CALVINIST': 'Calvinist',
      'CATHOLIC': 'Catholic',
      'CHRISTIAN': 'Christian',
      'CHURCH_OF_CHRIST': 'Church of Christ',
      'CHURCH_OF_GOD': 'Church of God',
      'CHURCH_OF_GOD_IN_CHRIST': 'Church of God in Christ',
      'CHURCH_OF_THE_NAZARENE': 'Church of the Nazarene',
      'DISCIPLES_OF_CHRIST': 'Disciples of Christ',
      'EPISCOPAL': 'Episcopal',
      'EVANGELICAL_LUTHERAN_CHURCH_IN_AMERICA': 'Evangelical Lutheran Church in America',
      'FRIENDS': 'Friends',
      'GREEK_ORTHODOX': 'Greek Orthodox',
      'ISLAMIC': 'Islamic',
      'JEWISH': 'Jewish',
      'LATTER_DAY_SAINTS': 'Latter Day Saints',
      'LUTHERAN_CHURCH_MISSOURI_SYNOD': 'Lutheran Church Missouri Synod',
      'MENNONITE': 'Mennonite',
      'METHODIST': 'Methodist',
      'NONSECTARIAN': 'Nonsectarian',
      'OTHER': 'Other',
      'OTHER_LUTHERAN': 'Other Lutheran',
      'PENTECOSTAL': 'Pentecostal',
      'PRESBYTERIAN': 'Presbyterian',
      'SEVENTH_DAY_ADVENTIST': 'Seventh Day Adventist',
      'WISCONSIN_EVANGELICAL_LUTHERAN_SYNOD': 'Wisconsin Evangelical Lutheran Synod'
    }[religiousAffiliation];
  },
  getHumanReadableType2(type2: string) {
    return {
      'ALTERNATIVE': 'Alternative',
      'EARLY_CHILDHOOD_DAY_CARE': 'Early Childhood Day Care',
      'MILITARY': 'Military',
      'MONTESSORI': 'Montessori',
      'SPECIAL_PROGRAM_EMPHASIS': 'Special Program Emphasis',
      'ONLINE': 'Online',
      'SPECIAL_EDUCATION': 'Special Education',
      'VOCATIONAL_TECHNICAL': 'Vocational Technical',
      'WALDORF': 'Waldorf',
      'ACUTE_CARE_DOD': 'Acuta Care, DoD',
      'ACUTE_CARE_HOSPITAL': 'Acute Care',
      'ACUTE_CARE_VETERANS': 'Acute Care, Veterans',
      'CHARTER': 'Charter',
      'CHILDRENS': `Children's hospital`,
      'CRITICAL_ACCESS': 'Critical Access',
      'PSYCHIATRIC': 'Psychiatric',
    }[type2];
  },
  getAdminTeamIds() {
    return [
      '62948182-2201-4c73-952c-35d56474c039',
      '326f0a9f-20d4-4f90-b2d9-ff61053fbb20'
    ]
  },
  /**
   * Teams that have access to the Buying Groups feature.
   * This controls both the sidebar visibility and route access.
   */
  getBuyingGroupsTeamIds(): string[] {
    return [
      '79d588df-fc70-4073-97b9-4b9496e2716c',
      '935e1b2f-9b3b-46f1-9dd9-a099ac02e3fb',
      '3d33fe3c-5bd2-4d2c-9c6a-7d6b24faa8a3',
      'ffd23d03-94e5-4c89-a459-e61f4840d69d',
      '5f322d17-9978-4293-900c-9ae9dd17f587',
      '4b06ce32-db0f-4456-a01d-5fa69f511e83',
      '780306b7-73ce-49b7-a9aa-23a90a25d2b1',
      'a80b27d0-ae07-45dd-87d6-d9bf534267f0',
      '63b87c34-6880-4a07-be2a-fa3c94f6f9f2',
      'abe7d12c-9e07-4156-9a03-6fcccb9d0815',
      '62948182-2201-4c73-952c-35d56474c039',
      '8143e3da-722b-49c4-9227-fab81fe81484',
    ]
  },
  /** The users the boss app's admin routes are open to (frontend-boss App.tsx, SideNav.tsx). */
  getSuperAdminUserIds() {
    return [
      '348f766e-e194-42ee-9ead-9e25d529cb17',
      'ebd5da1a-8a0c-4c4b-b4e5-293f977eeac8',
      '82b63315-08d3-4124-b1cc-dc5715e78c5e',
      '90dc2b07-38ec-459c-9b9d-69dab1e9f9b8'
    ]
  },
  getQ({ rules, combinator = 'and' }: { rules: any[], combinator?: 'and'|'or' }) {
    const q = {
      rules: rules.map(rule => {
        return {
          field: rule.field,
          operator: rule.operator,
          value: rule.value
        }
      }),
      combinator: combinator
    }
    return btoa(JSON.stringify(q))
  },
  formatDMM(dateString?: string | null) {
    try {
      if (!dateString) throw new Error('not a string')
      const dt = new Date(dateString)
      return `${dt.getMonth() + 1}/${dt.getDate()}`
    } catch {
      return '-'
    }
  },
  formatDate(dateString?: string | null) {
    try {
      if (!dateString) throw new Error('not a string')
      const dt = new Date(dateString)
      return `${dt.getMonth() + 1}/${dt.getDate()}/${dt.getFullYear()}`
    } catch {
      return '-'
    }
  },
  formatDateTime(dateString?: string | null) {
    try {
      if (!dateString) throw new Error('not a string')
      const dt = new Date(dateString);
      const hours = dt.getHours().toString().padStart(2, '0');
      const minutes = dt.getMinutes().toString().padStart(2, '0');
      const seconds = dt.getSeconds().toString().padStart(2, '0');
      return `${dt.getMonth() + 1}/${dt.getDate()}/${dt.getFullYear()} ${hours}:${minutes}:${seconds}`;
    } catch {
      return '-'
    }
  },
  formatDateTimeCompact(dateString?: string | null) {
    try {
      if (!dateString) throw new Error('not a string')
      const dt = new Date(dateString);
      const hours = dt.getHours().toString().padStart(2, '0');
      const minutes = dt.getMinutes().toString().padStart(2, '0');
      return `${dt.getMonth() + 1}/${dt.getDate()}/${dt.getFullYear().toString().slice(2)} ${hours}:${minutes}`;
    } catch {
      return '-'
    }
  },
  isOwner(globalContext: GlobalContextProps) {
    return globalContext.authInfo?.userAttributes?.profile === 'OWNER'
  },
  ellipsisInMiddle(str: string | undefined, ln = 56, delimiter = '...', startOffset = 20, endLength = 17) {
    if (!str) return str
    if (str?.length <= ln) return str
    const start = str.substring(0, ln - startOffset)
    const end = str.substring(str.length - endLength)
    return `${start}${delimiter}${end}`;
  },
  ellipsisAtEnd(str?: string | null, ln = 56, delimiter = '...') {
    if (!str) return str
    if (str?.length <= ln) return str
    const start = str.substring(0, ln - 3)
    return `${start}${delimiter}`;
  },
  getCSSFromId(id: string) {
    if (typeof id !== 'string') return 0
    return id.toLowerCase().charCodeAt(0) % 10
  },
  getInitials(name: string) {
    if (typeof name !== 'string') return '?'
    const segments = name.split(' ')
    let initials = '';
    if (segments[0]) initials += segments[0].substring(0, 1)
    if (segments[1]) initials += segments[1].substring(0, 1)
    return initials.length ? initials.toUpperCase() : '?'
  },
  async downloadUrl(s3Link: string, fileName: string) {
    if (typeof s3Link !== 'string') throw new Error('Invalid download link')
    if (typeof fileName !== 'string') throw new Error('Invalid file name')
    if (!s3Link.startsWith('https')) throw new Error('Invalid download link')

    const response = await fetch(s3Link);

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    const blob = await response.blob();

    const url = window.URL.createObjectURL(new Blob([blob]));
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  },
  deduplicateArrayByKey(arr: any[], key: string): object[] {
    const seen = new Set();
    return arr.filter((item) => {
      const keyValue = item[key];
      if (!seen.has(keyValue)) {
        seen.add(keyValue);
        return true;
      }
      return false;
    });
  },
  getCreatableStylesCompact(options?: { hasError?: boolean }) {
    const hasError = options?.hasError ?? false;
    return {
      multiValueLabel: (provided: any, state: any) => ({
        ...provided,
        fontSize: '100%',
        paddingTop: '1px',
        paddingRight: '8px',
        paddingBottom: '2px',
        paddingLeft: '8px',
        color: 'var(--amplify-colors-neutral-100)',
      }),
      multiValue: (provided: any, state: any) => ({
        ...provided,
        borderRadius: '15px',
        backgroundColor: 'var(--amplify-colors-neutral-25)',
        margin: '0 0 0 2px',
      }),
      singleValue: (provided: any, state: any) => ({
        ...provided,
        fontSize: '13px',
        color: 'var(--amplify-colors-neutral-100)',
        padding: '0 0 0 8px !important',
      }),
      control: (provided: any, state: any) => ({
        ...provided,
        padding: '0',
        minHeight: '30px',
        transition: 'none',
        backgroundColor: 'var(--amplify-colors-neutral-0)',
        borderColor: hasError
          ? 'var(--amplify-colors-red-60)'
          : state.isFocused
            ? 'var(--amplify-colors-purple-65)'
            : 'var(--amplify-components-tabs-border-color)',
        boxShadow: state.isFocused ? '0 0 0 3px var(--amplify-colors-purple-30)' : 'none',
        borderRadius: 'var(--amplify-components-button-border-radius)',
        '&:hover': {
          borderColor: hasError ? 'var(--amplify-colors-red-60)' : 'var(--amplify-colors-purple-65)',
        },
      }),
      valueContainer: (provided: any, state: any) => ({
        ...provided,
        padding: '0px !important',
        backgroundColor: 'var(--amplify-colors-neutral-0)',
        borderRadius: 'var(--amplify-components-button-border-radius)',
      }),
      placeholder: (provided: any, state: any) => ({
        ...provided,
        fontSize: '13px',
        marginLeft: '10px',
        color: 'var(--amplify-colors-neutral-50)',
      }),
      input: (provided: any, state: any) => ({
        ...provided,
        fontSize: '13px',
        marginLeft: '10px',
        padding: '0px',
        backgroundColor: 'transparent',
        color: 'var(--amplify-colors-neutral-100)'
      }),
      indicatorsContainer: (provided: any, state: any) => ({
        ...provided,
        padding: '0px 5px !important'
      }),
      dropdownIndicator: (provided: any, state: any) => ({
        ...provided,
        padding: '0px 5px !important',
        transition: 'none',
        color: 'var(--amplify-colors-neutral-45)',
        ':hover': {
          color: 'var(--amplify-colors-neutral-50)',
        },
      }),
      clearIndicator: (provided: any, state: any) => ({
        ...provided,
        padding: '0px 5px !important',
        transition: 'none',
        color: 'var(--amplify-colors-neutral-45)',
        ':hover': {
          color: 'var(--amplify-colors-neutral-50)',
        },
      }),
      indicatorSeparator: (provided: any, state: any) => ({
        ...provided,
        display: 'none'
      }),
      multiValueRemove: (provided: any, state: any) => ({
        ...provided,
        padding: '0px 1px !important'
      }),
      menu: (provided: any, state: any) => ({
        ...provided,
        marginTop: '2px',
        borderRadius: '8px',
        backgroundColor: 'var(--amplify-colors-neutral-0)',
        border: '1px solid var(--amplify-components-tabs-border-color)',
        boxShadow: 'none',
        zIndex: 5,
      }),
      option: (provided: any, state: any) => ({
        ...provided,
        fontSize: '13px',
        padding: '6px 12px',
        cursor: state.isDisabled ? 'not-allowed' : 'pointer',
        backgroundColor: state.isSelected
          ? 'transparent'
          : 'var(--amplify-colors-neutral-0)',
        color: state.isDisabled
          ? 'var(--amplify-colors-neutral-45)'
          : 'var(--amplify-colors-font-primary)',
        ':hover': {
          backgroundColor: state.isDisabled
            ? 'transparent'
            : 'var(--amplify-colors-neutral-20)',
        },
        ':active': {
          backgroundColor: 'transparent',
        },
      }),
    }
  },
  capitalizeFirstLetter(str: string) {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
  },
  capitalizeAllWords(str: string) {
    if (!str) return str;
    return str.split(' ').map(word => this.capitalizeFirstLetter(word)).join(' ');
  },
  reduceStringToNumber(str: string, max: number = 6): number {
    if (!str) {
      return 0;
    }

    let hash = 0;
  
    for (let i = 0; i < str.length; i++) {
      hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
    }
  
    return hash % max;
  },
  replaceNonAlphanumericWithDash(str: string) {
    return str.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-').toLowerCase();
  },
  epochToHumanReadableDate2(epoch: number | string | null | undefined) {
    if (!epoch) return ''
    return new Date(epoch).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
  },
  async getAuthorizationHeaders() {
    const authSession = await fetchAuthSession()
    const token = authSession.tokens?.idToken
    if (!token) throw new Error('Failed to retrieve ID token')
    return { Authorization: token.toString() };
  },
  addCommasToNumber(number: number) {
    return number?.toLocaleString();
  },
  formatCompactNumber(number: number) {
    return new Intl.NumberFormat('en', { notation: 'compact' }).format(number);
  },
  isLocalhost() {
    try {
      return window.location.hostname === 'localhost'
    } catch {
      return false
    }
  },
  getQueryParamValueOrNull(queryParams: URLSearchParams, paramName: string) {
    try {
      const val = queryParams.get(paramName) || null
      if (!val) return val
      if (val === 'true') return true
      if (val === 'false') return false
      if (!isNaN(parseInt(val))) return parseInt(val)
      return val
    } catch {
      return ''
    }
  },
  trim(str: string, length: number) {
    if (typeof str !== 'string') return str
    if (str.length > length) {
      return `${str.substring(0, length).trim()}...`
    }
    return str;
  },
  getModalRoot(): HTMLElement {
    return document.getElementById('modalroot') as HTMLElement
  },
  getSliderDefaults({ padding = '0', backgroundColor = 'var(--amplify-colors-neutral-0)', width = '400px' }: { padding?: string, backgroundColor?: string, width?: string } = {}) {
    return {
      content: {
        top: 0,
        left: 'auto',
        right: 0,
        bottom: 0,
        width: width,
        border: 'none',
        overflow: 'auto',
        borderRadius: 0,
        outline: 'none',
        padding: padding,
        backgroundColor: backgroundColor
      },
      overlay: {
        background: 'var(--overlay-bg)'
      }
    }
  },
  getModalDefaults({
    padding = '20px',
    width = '600px',
    height = 'auto',
    backgroundColor = 'var(--amplify-colors-neutral-0)',
    overflow = 'auto'
  }: { padding?: string, width?: string, height?: string, backgroundColor?: string, overflow?: string } = {}) {
    return {
      content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        width: width,
        maxWidth: 'calc(100vw - 20px)',
        height: height,
        maxHeight: 'calc(100vh - 20px)',
        borderRadius: 20,
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        overflow: overflow,
        border: 'none',
        padding: padding,
        backgroundColor: backgroundColor
      },
      overlay: {
        background: 'var(--overlay-bg)'
      }
    }
  },
  getTrueKeys(obj: object): Array<[string, any]> {
    return Object
      .entries(obj)
      .filter(item => !!item[1])
  },
  getRandomString(length: number) {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      result += characters.charAt(randomIndex);
    }
    return result;
  },
  setTitle: (document: Document, title: string, globalContext: GlobalContextProps) => {
    document.title = [title, globalContext.environment.name].filter(Boolean).join(' / ');
  },
  encode: (object: object | undefined) => {
    try {
      return Buffer.from(JSON.stringify(object)).toString('base64');
    } catch (e) {
      return null
    }
  },
  decode: (object: string | undefined) => {
    try {
      if (!object) return null;
      return JSON.parse(Buffer.from(object, 'base64').toString());
    } catch (e) {
      return null
    }
  },
  getStateName: (stateId: string | null) => {
    if (!stateId) return null;
    return {
      AL: 'Alabama',
      AK: 'Alaska',
      AS: 'American Samoa',
      AZ: 'Arizona',
      AR: 'Arkansas',
      CA: 'California',
      CO: 'Colorado',
      CT: 'Connecticut',
      DE: 'Delaware',
      DC: 'District Of Columbia',
      FM: 'Federated States Of Micronesia',
      FL: 'Florida',
      GA: 'Georgia',
      GU: 'Guam',
      HI: 'Hawaii',
      ID: 'Idaho',
      IL: 'Illinois',
      IN: 'Indiana',
      IA: 'Iowa',
      KS: 'Kansas',
      KY: 'Kentucky',
      LA: 'Louisiana',
      ME: 'Maine',
      MH: 'Marshall Islands',
      MD: 'Maryland',
      MA: 'Massachusetts',
      MI: 'Michigan',
      MN: 'Minnesota',
      MS: 'Mississippi',
      MO: 'Missouri',
      MT: 'Montana',
      NE: 'Nebraska',
      NV: 'Nevada',
      NH: 'New Hampshire',
      NJ: 'New Jersey',
      NM: 'New Mexico',
      NY: 'New York',
      NC: 'North Carolina',
      ND: 'North Dakota',
      MP: 'Northern Mariana Islands',
      OH: 'Ohio',
      OK: 'Oklahoma',
      OR: 'Oregon',
      PW: 'Palau',
      PA: 'Pennsylvania',
      PR: 'Puerto Rico',
      RI: 'Rhode Island',
      SC: 'South Carolina',
      SD: 'South Dakota',
      TN: 'Tennessee',
      TX: 'Texas',
      UT: 'Utah',
      VT: 'Vermont',
      VI: 'Virgin Islands',
      VA: 'Virginia',
      WA: 'Washington',
      WV: 'West Virginia',
      WI: 'Wisconsin',
      WY: 'Wyoming'
    }[stateId];
  },
  getGradesObject: (it: string) => {
    return {
      // "g_nursery": "Nursery",
      "g_pk": "Pre-K",
      "g_kg": "Kindergarten",
      "g_1": "1st Grade",
      "g_2": "2nd Grade",
      "g_3": "3rd Grade",
      "g_4": "4th Grade",
      "g_5": "5th Grade",
      "g_6": "6th Grade",
      "g_7": "7th Grade",
      "g_8": "8th Grade",
      "g_9": "9th Grade",
      "g_10": "10th Grade",
      "g_11": "11th Grade",
      "g_12": "12th Grade",
    }[it]
  },
  getGradesForSelect: () => {
    return [
      // { name: "g_nursery", label: "Nursery" },
      { name: "g_pk", label: "Pre-K" },
      { name: "g_kg", label: "Kindergarten" },
      { name: "g_1", label: "1st Grade" },
      { name: "g_2", label: "2nd Grade" },
      { name: "g_3", label: "3rd Grade" },
      { name: "g_4", label: "4th Grade" },
      { name: "g_5", label: "5th Grade" },
      { name: "g_6", label: "6th Grade" },
      { name: "g_7", label: "7th Grade" },
      { name: "g_8", label: "8th Grade" },
      { name: "g_9", label: "9th Grade" },
      { name: "g_10", label: "10th Grade" },
      { name: "g_11", label: "11th Grade" },
      { name: "g_12", label: "12th Grade" },
    ]
  },
  getCountiesForSelect: () => {
    const arr = []
    for (const key in counties) arr.push({ name: key, label: (counties as any)[key] })
    return arr
  },
  getCountiesObject: (it: string) => {
    return (counties as any)[it]
  },
  /** Whether a team's tier is a paying one (monthly or annual), not a trial. */
  isPaidTier(tier?: string) {
    return General.getTier(tier)?.tier === 'CUSTOMER';
  },
  getTier(tier?: string) {
    if (!tier) return null;
    if (tier?.startsWith('ANNUAL')) {
      return {
        tier: 'CUSTOMER',
        humanReadable: '★★★'
      }
    }
    if (tier?.startsWith('MONTHLY')) {
      return {
        tier: 'CUSTOMER',
        humanReadable: '★★'
      }
    }
    if (tier === 'TRIAL') {
      return {
        tier,
        humanReadable: 'Trial'
      }
    }
    if (tier === 'EXPIRED_TRIAL') {
      return {
        tier,
        humanReadable: 'Trial Expired'
      }
    }
  },
  getStatesForSelect: () => {
    return [
      {
        "label": "Alabama",
        "value": "AL"
      },
      {
        "label": "Alaska",
        "value": "AK"
      },
      {
        "label": "Arizona",
        "value": "AZ"
      },
      {
        "label": "Arkansas",
        "value": "AR"
      },
      {
        "label": "California",
        "value": "CA"
      },
      {
        "label": "Colorado",
        "value": "CO"
      },
      {
        "label": "Connecticut",
        "value": "CT"
      },
      {
        "label": "Delaware",
        "value": "DE"
      },
      {
        "label": "District Of Columbia",
        "value": "DC"
      },
      {
        "label": "Florida",
        "value": "FL"
      },
      {
        "label": "Georgia",
        "value": "GA"
      },
      {
        "label": "Hawaii",
        "value": "HI"
      },
      {
        "label": "Idaho",
        "value": "ID"
      },
      {
        "label": "Illinois",
        "value": "IL"
      },
      {
        "label": "Indiana",
        "value": "IN"
      },
      {
        "label": "Iowa",
        "value": "IA"
      },
      {
        "label": "Kansas",
        "value": "KS"
      },
      {
        "label": "Kentucky",
        "value": "KY"
      },
      {
        "label": "Louisiana",
        "value": "LA"
      },
      {
        "label": "Maine",
        "value": "ME"
      },
      {
        "label": "Maryland",
        "value": "MD"
      },
      {
        "label": "Massachusetts",
        "value": "MA"
      },
      {
        "label": "Michigan",
        "value": "MI"
      },
      {
        "label": "Minnesota",
        "value": "MN"
      },
      {
        "label": "Mississippi",
        "value": "MS"
      },
      {
        "label": "Missouri",
        "value": "MO"
      },
      {
        "label": "Montana",
        "value": "MT"
      },
      {
        "label": "Nebraska",
        "value": "NE"
      },
      {
        "label": "Nevada",
        "value": "NV"
      },
      {
        "label": "New Hampshire",
        "value": "NH"
      },
      {
        "label": "New Jersey",
        "value": "NJ"
      },
      {
        "label": "New Mexico",
        "value": "NM"
      },
      {
        "label": "New York",
        "value": "NY"
      },
      {
        "label": "North Carolina",
        "value": "NC"
      },
      {
        "label": "North Dakota",
        "value": "ND"
      },
      {
        "label": "Ohio",
        "value": "OH"
      },
      {
        "label": "Oklahoma",
        "value": "OK"
      },
      {
        "label": "Oregon",
        "value": "OR"
      },
      {
        "label": "Palau",
        "value": "PW"
      },
      {
        "label": "Pennsylvania",
        "value": "PA"
      },
      {
        "label": "Puerto Rico",
        "value": "PR"
      },
      {
        "label": "Rhode Island",
        "value": "RI"
      },
      {
        "label": "South Carolina",
        "value": "SC"
      },
      {
        "label": "South Dakota",
        "value": "SD"
      },
      {
        "label": "Tennessee",
        "value": "TN"
      },
      {
        "label": "Texas",
        "value": "TX"
      },
      {
        "label": "Utah",
        "value": "UT"
      },
      {
        "label": "Vermont",
        "value": "VT"
      },
      {
        "label": "Virgin Islands",
        "value": "VI"
      },
      {
        "label": "Virginia",
        "value": "VA"
      },
      {
        "label": "Washington",
        "value": "WA"
      },
      {
        "label": "West Virginia",
        "value": "WV"
      },
      {
        "label": "Wisconsin",
        "value": "WI"
      },
      {
        "label": "Wyoming",
        "value": "WY"
      }
    ]
  },
  formatMonthYear(isoString: string): string {
    try {
      const date = new Date(isoString);
      const options: Intl.DateTimeFormatOptions = { month: 'long', year: 'numeric' };
      return date.toLocaleDateString('en-US', options);
    } catch (err) {
      console.error('Error formatting date:', err);
      return '';
    }
  },
  normalizeUrl(url: string): string | null {
    if (!url) return null;

    let normalized = url.replace(/^(https?:\/\/)?(www\.)?/, '');

    // Strip unwanted trailing chars
    for (let i = 0; i < 2; i++) {
      for (const pattern of [/#!$/i, /&$/i, /#$/i, /\/$/i, /,$/]) {
        normalized = normalized.replace(pattern, '');
      }
    }

    return normalized;
  }
}

export default General;